import { create } from 'zustand';
import queryClient from '../services/queryClient';
import useChatStore, { getPendingGenerationForThread } from './useChatStore';
import {
  TAiAgentDetails,
    THREAD_DEFAULT_LANGUAGE_MODEL,
    TMessage,
    TMessageProperties,
    TMessageResponse,
} from '@core/shared/types';
import useHistoryStore from './useHistoryStore';
import { logger } from '@shared/logger';
import { handleError } from '@shared/handleError';

export const OPTIMISTIC_MESSAGE_ID = 'OPTIMISTIC_MESSAGE_ID';
export const MESSAGESTORE_KEY = 'MessageStore';

export const AWAIT_AI_MESSAGE_RESPONSE_KEY = 'MessageStoreAIResponse';

const getThreadIdFromCurrentUrl = (routePath?: string): string | undefined => {
    if (typeof window === 'undefined') return undefined;

    const pathSegments = window.location.pathname.split('/').filter(Boolean);
    const routeIndex = routePath ? pathSegments.indexOf(routePath) : -1;
    const urlThreadId = routeIndex >= 0 ? pathSegments[routeIndex + 1] : pathSegments[1];

    return urlThreadId ? decodeURIComponent(urlThreadId) : undefined;
};

const getRoutePathFromCurrentUrl = (): string | undefined => {
    if (typeof window === 'undefined') return undefined;

    return window.location.pathname.split('/').filter(Boolean)[0];
};

const isLangdockRoute = (routePath?: string): boolean =>
    Boolean(routePath?.startsWith('langdock-poc'));

type TMessageStore = {
    messages: TMessage[] | null;
    messageProperties: TMessageProperties | null;
    cachedMessages: Record<string, TMessage[] | null>;
    setMessageProperties: (properties: TMessageProperties | null) => void;
    setMessages: (messages: TMessage[] | null) => void;
    getMessages: () => Promise<void>;
    postMessage: (aiAgent: TAiAgentDetails | undefined, messageText: string, isDeepResearch?: boolean, isPreview?: boolean, requestId?: string) => Promise<TMessageResponse | null | undefined>;
    addMessageOptimistic: (message: string) => void;
    clearMessages: () => void;
    pruneOptimisticMessage: () => void;
    restorePendingOptimisticMessage: (threadId: string | null) => void;
    setCachedMessagesByThreadId: (threadId: string, messages: TMessage[] | null) => void;
    getTemporaryPreviewUrl: (path: string) => Promise<string>;
};

/**
 * Zustand store for managing messages.
 */
const useMessageStore = create<TMessageStore>()((set, get) => ({
    messages: null,
    messageProperties: null,
    cachedMessages: {},

    /**
     * Sets the messages in the store.
     * @param {TMessage[] | null} messages - The messages to set in the store.
     * @returns {void}
     */
    setMessages: (messages: TMessage[] | null): void => {
        logger.log(MESSAGESTORE_KEY, 1, 'Setting messages:', messages);
        set({ messages });
    },

    setCachedMessagesByThreadId: (threadId: string, messages: TMessage[] | null): void => {
        logger.log(
            MESSAGESTORE_KEY,
            1,
            'Setting cached messages for thread ID:',
            threadId,
            messages,
        );
        set((state) => ({
            cachedMessages: {
                ...state.cachedMessages,
                [threadId]: messages,
            },
        }));
    },

    /**
     * Fetches messages for the current thread.
     * @returns {Promise<void>} A promise that resolves when messages have been fetched.
     */
    getMessages: async (): Promise<void> => {
        try {
            const threadId = useChatStore.getState().threadId;
            const routeConfig = useChatStore.getState().routeConfig;

            if (!threadId) {
                logger.warn(MESSAGESTORE_KEY, 1, 'No thread ID set. Cancelling request.');
                return;
            }

            if (get().cachedMessages[threadId]) {
                logger.log(
                    MESSAGESTORE_KEY,
                    1,
                    'Cached messages available for thread ID, compare them with the request:',
                    threadId,
                );
            }
            logger.log(MESSAGESTORE_KEY, 1, 'Fetching messages for thread ID:', threadId);

            const routePath = routeConfig.routePath;
            const queryKey = [
                MESSAGESTORE_KEY,
                routePath,
                threadId,
                get().cachedMessages[threadId],
            ];

            logger.log(MESSAGESTORE_KEY, 1, 'Cached items: ', queryClient.getQueryData(queryKey));
            useChatStore.getState().setLoadingState(MESSAGESTORE_KEY, true);

            await queryClient.cancelQueries({ queryKey });

            const messagesResponse = await queryClient.fetchQuery({
                queryKey,
                queryFn: () => routeConfig.apiClient.getMessages(threadId),
            });

            logger.log(MESSAGESTORE_KEY, 1, 'Fetched messages:', messagesResponse);
            set({ messages: messagesResponse });
            get().restorePendingOptimisticMessage(threadId);

            //Caching: update the cache with the new messages
            const newQueryKey = [MESSAGESTORE_KEY, routePath, threadId, messagesResponse.messages];
            queryClient.setQueryData(newQueryKey, messagesResponse);
            useChatStore.getState().setError(MESSAGESTORE_KEY, null);
            //Caching End
        } catch (error) {
            await handleError(error, MESSAGESTORE_KEY);
        } finally {
            useChatStore.getState().setLoadingState(MESSAGESTORE_KEY, false);
        }
    },

    /**
     * Adds an optimistic message to the store.
     * Optimistic messages are used to immediately display the message in the UI
     * before the server responds.
     * @param {string} message - The message text to add as optimistic.
     * @returns {void}
     */
    addMessageOptimistic: (message: string): void => {
        const optimisticMessage: TMessage = {
            thread_id: useChatStore.getState().threadId || null,
            message_id: OPTIMISTIC_MESSAGE_ID,
            user_message: message,
            properties: get().messageProperties || null,
            rag_type: 'adaptive',
            ai_message: null,
            language_model: THREAD_DEFAULT_LANGUAGE_MODEL,
            updated: new Date().toISOString(),
            created: new Date().toISOString(),
        };

        logger.log(MESSAGESTORE_KEY, 1, 'Adding optimistic message:', optimisticMessage);
        set((state) => ({
            messages: state.messages ? [...state.messages, optimisticMessage] : [optimisticMessage],
        }));
    },

    /**
     * Prunes optimistic messages from the store.
     * This removes the message with the optimistic message ID to ensure the state
     * reflects the actual server response.
     * @returns {void}
     */
    pruneOptimisticMessage: (): void => {
        logger.log(MESSAGESTORE_KEY, 1, 'Pruning optimistic messages');
        set((state) => ({
            messages: state.messages?.filter(
                (message) => message.message_id !== OPTIMISTIC_MESSAGE_ID,
            ),
        }));
    },

    restorePendingOptimisticMessage: (threadId: string | null): void => {
        const pending = getPendingGenerationForThread(
            useChatStore.getState().pendingGenerations,
            threadId,
        );

        if (!pending) return;

        set((state) => {
            const messages = state.messages ?? [];
            const alreadyPresent = messages.some(
                (message) =>
                    message.message_id === OPTIMISTIC_MESSAGE_ID ||
                    (message.user_message === pending.messageText && !message.ai_message),
            );

            if (alreadyPresent) return state;

            const optimisticMessage: TMessage = {
                thread_id: threadId,
                message_id: OPTIMISTIC_MESSAGE_ID,
                user_message: pending.messageText,
                properties: state.messageProperties || null,
                rag_type: 'adaptive',
                ai_message: null,
                language_model: THREAD_DEFAULT_LANGUAGE_MODEL,
                updated: new Date().toISOString(),
                created: new Date().toISOString(),
            };

            logger.log(MESSAGESTORE_KEY, 1, 'Restoring pending optimistic message for thread:', threadId);
            return { messages: [...messages, optimisticMessage] };
        });
    },

    /**
     * Clears all messages from the store.
     * @returns {void}
     */
    clearMessages: (): void => {
        logger.log(MESSAGESTORE_KEY, 1, 'Clearing all messages');
        set({ messages: null });
    },

    /**
     * Posts a message to the server and adds it to the store.
     * @param {string} messageText - The text of the message to post to the server.
     * @returns {Promise<TMessageResponse | null | undefined>} A promise that resolves to the message response
     * from the server or `undefined` if an error occurs.
     */
    postMessage: async (aiAgent: TAiAgentDetails | undefined, messageText: string, isDeepResearch?: boolean, isPreview?: boolean, requestId?: string): Promise<TMessageResponse | null | undefined> => {
        const generationRequestId = requestId || `gen_${Date.now()}_${Math.random().toString(36).slice(2)}`;
        try {
            const threadId = useChatStore.getState().threadId;
            const routeConfig = useChatStore.getState().routeConfig;
            const threadIdForRequest = threadId || getThreadIdFromCurrentUrl(routeConfig.routePath);
            const routePathForRequest = getRoutePathFromCurrentUrl() || routeConfig.routePath;
            const isLangdockPoc = isLangdockRoute(routePathForRequest);

            logger.log(MESSAGESTORE_KEY, 1, 'Sending message for thread ID:', threadIdForRequest);

            useChatStore.getState().setLoadingState(AWAIT_AI_MESSAGE_RESPONSE_KEY, true);
            if (!isPreview) {
                useChatStore.getState().startGeneration(generationRequestId, threadIdForRequest || null, messageText);
            }

            let messageObject: TMessage;

            if (isPreview && !!aiAgent) {
              messageObject = {
                user_message: messageText,
                system_prompt: aiAgent.system_prompt,
                document_repositories: aiAgent.document_repositories,
                ai_model: aiAgent.ai_model,
                is_deep_research: isDeepResearch || false
              };
            }
            else {
              messageObject = {
                  ai_assistant: aiAgent?.id,
                  thread_id: threadIdForRequest || null,
                  message_id: null,
                  user_message: messageText,
                  language_model: THREAD_DEFAULT_LANGUAGE_MODEL,
                  properties: get().messageProperties || null,
                  rag_type: 'adaptive',
                  is_deep_research: isDeepResearch || false,  // Add this line
              };

              if (!threadIdForRequest && !isPreview) {
                  logger.log(
                      MESSAGESTORE_KEY,
                      1,
                      'No thread ID set. Adding optimistic history thread.',
                  );
                  useHistoryStore.getState().addHistoryThreadOptimistic();
              }
              //useMessageStore.getState().addMessageOptimistic(messageText);
            }

            const messageResponse: TMessageResponse = await queryClient.fetchQuery({
                queryKey: [MESSAGESTORE_KEY, Math.random().toString()], //no need, because we do not cache this query
                queryFn: () => routeConfig.apiClient.postMessage(messageObject, { isLangdockPoc }),
                staleTime: 0, // Never cache this query
            });

            const mergedMessageResponse: TMessageResponse = messageResponse;

            logger.log(MESSAGESTORE_KEY, 1, 'Message sent successfully:', mergedMessageResponse);
            const responseThreadId = mergedMessageResponse.thread_id;

            if (responseThreadId) {
                if (!isPreview) {
                    useChatStore.getState().resolveGenerationThread(generationRequestId, responseThreadId);
                }

                // Only claim the globally shared ai_thread_id when the user is still looking at
                // this thread, otherwise a background response would misattribute later feedback.
                const liveThreadId =
                    useChatStore.getState().threadId ||
                    getThreadIdFromCurrentUrl(useChatStore.getState().routeConfig.routePath);

                if (liveThreadId === responseThreadId || !liveThreadId) {
                    try {
                        localStorage.setItem('ai_thread_id', responseThreadId);
                    } catch (err) {
                        logger.warn(MESSAGESTORE_KEY, 1, 'Could not write ai_thread_id to localStorage', err);
                    }
                }

                useHistoryStore
                    .getState()
                    .reviveOptimisticThread(responseThreadId, messageText);

                //Update the Cache with the new messages
                logger.log(
                    MESSAGESTORE_KEY,
                    1,
                    'Updating cache with new messages:',
                    mergedMessageResponse.messages,
                );

                const newQueryKey = [
                    MESSAGESTORE_KEY,
                    routeConfig.routePath,
                    responseThreadId,
                    mergedMessageResponse.messages,
                ];
                queryClient.setQueryData(newQueryKey, mergedMessageResponse.messages);
                get().setCachedMessagesByThreadId(responseThreadId, mergedMessageResponse.messages);

                return mergedMessageResponse;
            }

            logger.warn(
                MESSAGESTORE_KEY,
                1,
                'Message response has no thread_id. Skipping thread-bound cache/history updates.',
            );
            useHistoryStore.getState().pruneOptimisticThread();

            return mergedMessageResponse;
        } catch (error) {
            await handleError(error, MESSAGESTORE_KEY);

            useHistoryStore.getState().pruneOptimisticThread();
            useMessageStore.getState().pruneOptimisticMessage();

            return undefined;
        } finally {
            useChatStore.getState().setLoadingState(AWAIT_AI_MESSAGE_RESPONSE_KEY, false);
            useChatStore.getState().finishGeneration(generationRequestId);
        }
    },

    /**
     * Retrieves a temporary preview URL for a file to preview it.
     * @param path
     */
    getTemporaryPreviewUrl: async (path: string): Promise<string> => {
        try {
            const routeConfig = useChatStore.getState().routeConfig;
            let sanitizedPath = path;

            // Backend validation rejects fragment identifiers in file_url.
            if (typeof path === 'string' && path.includes('#')) {
                try {
                    if (path.startsWith('http://') || path.startsWith('https://')) {
                        const parsed = new URL(path);
                        sanitizedPath = `${parsed.origin}${parsed.pathname}${parsed.search}`;
                    } else {
                        sanitizedPath = path.split('#')[0];
                    }
                } catch {
                    sanitizedPath = path.split('#')[0];
                }
            }

            return await routeConfig.apiClient.getTemporaryFileUrl(sanitizedPath);
        } catch (error) {
            await handleError(error, MESSAGESTORE_KEY);
            return '';
        }
    },

    /**
     * Sets the message properties for the next posted message.
     * @param properties
     */
    setMessageProperties: (properties: TMessageProperties | null): void => {
        logger.log(MESSAGESTORE_KEY, 2, 'Setting message properties:', properties);
        set({ messageProperties: properties });
    },
}));

export default useMessageStore;
